import { Box, Flex, Heading, Stack } from "@biblioteksentralen/react";
import { LibraryData } from "../../app/[locale]/sites/[siteDomain]/bibliotek/[bibliotek]/page";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { DefaultContainer } from "../ContentContainer";
import { EventsList } from "../arrangement/EventsList";
import { eventsListSelectedLibrariesFilterKey } from "../arrangement/EventsWithFilters";
import Contactinfo from "../contactInfo/ContactInfo";
import SocialMediaView from "../contactInfo/SocialMedia";
import { Edit } from "../editInSanity/EditInSanity";
import { SanityImageWithResponsiveSize } from "../images/SanityImageWithResponsiveSize";
import { useCommonData } from "../layout/CommonDataProvider";
import LibraryOpeningHours from "../openingHours/LibraryOpeningHours";
import SeMerLenke from "../site/SeMerLenke";
import { FeaturedBanner } from "../site/banners/promotedItems/FeaturedBanner";
import AccessibilityInfoView from "./AccessibilityInfoView";

type Props = LibraryData;

export function LibraryView(props: Props) {
  const { t, ts } = useTranslation();
  const { library } = props;
  const { site } = useCommonData();

  if (!library) return null;

  const socialMedia = library.socialMedia?.useSiteSocialMedia ? site?.socialMedia : library.socialMedia;

  return (
    <DefaultContainer>
      <Stack spacing="5rem">
        <Stack direction="column" spacing="1.5rem">
          <Heading as="h1">{ts(library.name)}</Heading>
          <Edit doc={library} />
          <Stack direction="column" spacing="3rem">
            <SanityImageWithResponsiveSize
              image={library.image}
              aspectRatio={2.6}
              resolution={{ base: 520, sm: 1040, lg: 2080 }}
            />
            <Flex justify="space-between" wrap="wrap" gridGap={{ base: "2rem", md: "5rem" }}>
              <Box flex="1 1 20rem" maxWidth="25rem">
                <Heading as="h2" size="md">
                  {t("Åpningstider")}
                </Heading>
                <LibraryOpeningHours library={library} />
              </Box>
              <Stack as="section" direction="column" spacing="2rem" flex="1 1 15rem">
                <Heading as="h2" size="md">
                  {t("Om biblioteket")}
                </Heading>
                <Box>{ts(library.teaser)}</Box>
                <Contactinfo contactInfo={library.contactInfo} />
                <SocialMediaView socialMedia={socialMedia} marginTop="1rem !important" />
                <AccessibilityInfoView accessibilityInfo={library.accessibilityInfo} />
              </Stack>
            </Flex>
          </Stack>
        </Stack>
        <Events {...props} />
      </Stack>
    </DefaultContainer>
  );
}

const Events = (props: Props) => {
  const { t, ts } = useTranslation();
  const moreEventsUrl = useMoreEventsUrl(props.library);

  if (!props.events.length) return null;

  return (
    <Stack spacing="1rem">
      <Heading as="h2" size="md">
        {t("Arrangementer på {navn}", { navn: ts(props.library.name) })}
      </Heading>
      <FeaturedBanner featuredItems={props.promotedEvents} />
      <EventsList marginTop={props.promotedEvents.length ? "2rem" : undefined} events={props.events} />
      <SeMerLenke href={moreEventsUrl} alignSelf="flex-end">
        {t("Se flere arrangementer på {navn}", { navn: ts(props.library.name) })}
      </SeMerLenke>
    </Stack>
  );
};

const useMoreEventsUrl = (library: Props["library"]) => {
  const libraryCount = useCommonData().headerFooterData?.libraries.length ?? 1;

  const libraryFilter =
    // Vi legger bare på filial-filter dersom biblioteket har flere filialer
    libraryCount > 1 && `?${eventsListSelectedLibrariesFilterKey}=${JSON.stringify([library.slug])}`;
  return `/arrangementer${libraryFilter || ""}`;
};
